import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ArrowRight, Bot, Sparkles, Globe, TrendingUp, Users, Zap } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { updateMetaTags } from '@/utils/seoUtils';

const AIInfluencerService = () => {
  const { currentLanguage } = useLanguage();
  const { city } = useParams<{ city?: string }>();

  useEffect(() => {
    const title = {
      de: `AI Influencer Creation ${city ? `in ${city}` : 'Schweiz'} | Future Level`,
      en: `AI Influencer Creation ${city ? `in ${city}` : 'Switzerland'} | Future Level`,
      fr: `Création d'Influenceurs IA ${city ? `à ${city}` : 'Suisse'} | Future Level`,
      it: `Creazione Influencer IA ${city ? `a ${city}` : 'Svizzera'} | Future Level`
    };

    const description = {
      de: `Virtuelle Markenbotschafter mit KI erstellen. 24/7 Content, mehrsprachig, skalierbar. Revolutionäres Influencer Marketing für Schweizer Unternehmen.`,
      en: `Create virtual brand ambassadors with AI. 24/7 content, multilingual, scalable. Revolutionary influencer marketing for Swiss businesses.`,
      fr: `Créez des ambassadeurs de marque virtuels avec IA. Contenu 24/7, multilingue, évolutif. Marketing d'influence révolutionnaire pour entreprises suisses.`,
      it: `Crea ambasciatori virtuali del brand con IA. Contenuto 24/7, multilingue, scalabile. Marketing influencer rivoluzionario per aziende svizzere.`
    };

    updateMetaTags({
      title: title[currentLanguage],
      description: description[currentLanguage],
      canonicalUrl: `https://futurelevel.ch/${currentLanguage}/ai-influencer${city ? `/${city}` : ''}/`,
      language: currentLanguage
    });
  }, [currentLanguage, city]);

  const services = [
    {
      icon: Bot,
      title: {
        de: 'Virtueller Markenbotschafter',
        en: 'Virtual Brand Ambassador',
        fr: 'Ambassadeur de Marque Virtuel',
        it: 'Ambasciatore Virtuale del Brand'
      },
      description: {
        de: 'Personalisierte AI-Influencer, die Ihre Marke authentisch repräsentieren',
        en: 'Personalized AI influencers that authentically represent your brand',
        fr: 'Influenceurs IA personnalisés qui représentent authentiquement votre marque',
        it: 'Influencer IA personalizzati che rappresentano autenticamente il vostro brand'
      },
      price: 'CHF 2,500-5,000'
    },
    {
      icon: Sparkles,
      title: {
        de: '24/7 Content Creation',
        en: '24/7 Content Creation',
        fr: 'Création de Contenu 24/7',
        it: 'Creazione Contenuto 24/7'
      },
      description: {
        de: 'Kontinuierliche Content-Produktion ohne Pausen oder Urlaub',
        en: 'Continuous content production without breaks or vacations',
        fr: 'Production continue de contenu sans pauses ni vacances',
        it: 'Produzione continua di contenuti senza pause o vacanze'
      },
      price: 'CHF 800-1,500/Monat'
    },
    {
      icon: Globe,
      title: {
        de: 'Mehrsprachige Kampagnen',
        en: 'Multilingual Campaigns',
        fr: 'Campagnes Multilingues',
        it: 'Campagne Multilingue'
      },
      description: {
        de: 'Ein Influencer spricht perfekt Deutsch, Französisch, Italienisch & Englisch',
        en: 'One influencer speaks perfect German, French, Italian & English',
        fr: 'Un influenceur parle parfaitement allemand, français, italien et anglais',
        it: 'Un influencer parla perfettamente tedesco, francese, italiano e inglese'
      },
      price: 'CHF 1,200-2,500/Kampagne'
    }
  ];

  const useCases = [
    {
      title: {
        de: 'E-Commerce',
        en: 'E-Commerce',
        fr: 'E-Commerce',
        it: 'E-Commerce'
      },
      description: {
        de: 'Produktvorstellungen, Unboxings, Shopping-Hauls',
        en: 'Product showcases, unboxings, shopping hauls',
        fr: 'Présentations produits, déballages, achats groupés',
        it: 'Showcase prodotti, unboxing, shopping haul'
      },
      roi: '+340% Conversions'
    },
    {
      title: {
        de: 'Fintech',
        en: 'Fintech',
        fr: 'Fintech',
        it: 'Fintech'
      },
      description: {
        de: 'Finanzbildung, App-Tutorials, Vertrauensaufbau',
        en: 'Financial education, app tutorials, trust building',
        fr: 'Éducation financière, tutoriels app, création confiance',
        it: 'Educazione finanziaria, tutorial app, costruzione fiducia'
      },
      roi: '+280% App Downloads'
    },
    {
      title: {
        de: 'B2B SaaS',
        en: 'B2B SaaS',
        fr: 'B2B SaaS',
        it: 'B2B SaaS'
      },
      description: {
        de: 'Thought Leadership, LinkedIn Content, Webinar-Promotion',
        en: 'Thought leadership, LinkedIn content, webinar promotion',
        fr: 'Leadership éclairé, contenu LinkedIn, promotion webinaires',
        it: 'Thought leadership, contenuti LinkedIn, promozione webinar'
      },
      roi: '+450% Lead Quality'
    }
  ];

  const advantages = [
    {
      icon: TrendingUp,
      title: {
        de: 'Kosteneffizienz',
        en: 'Cost Efficiency',
        fr: 'Efficacité Coût',
        it: 'Efficienza Costi'
      },
      description: {
        de: '90% günstiger als echte Influencer mit vergleichbarer Reichweite',
        en: '90% cheaper than real influencers with comparable reach',
        fr: '90% moins cher que vrais influenceurs avec portée comparable',
        it: '90% più economico di influencer reali con portata comparabile'
      }
    },
    {
      icon: Users,
      title: {
        de: 'Markenkontrolle',
        en: 'Brand Control',
        fr: 'Contrôle Marque',
        it: 'Controllo Brand'
      },
      description: {
        de: 'Keine Skandale, perfekte Markensicherheit, vollständige Kontrolle',
        en: 'No scandals, perfect brand safety, complete control',
        fr: 'Pas de scandales, sécurité marque parfaite, contrôle total',
        it: 'Nessuno scandalo, sicurezza brand perfetta, controllo completo'
      }
    },
    {
      icon: Zap,
      title: {
        de: 'Skalierbarkeit',
        en: 'Scalability',
        fr: 'Évolutivité',
        it: 'Scalabilità'
      },
      description: {
        de: 'Unbegrenzte Content-Produktion, mehrere Plattformen gleichzeitig',
        en: 'Unlimited content production, multiple platforms simultaneously',
        fr: 'Production contenu illimitée, plusieurs plateformes simultanément',
        it: 'Produzione contenuti illimitata, più piattaforme simultaneamente'
      }
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-background to-background/80 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-background to-background"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-4">
                <Bot className="w-4 h-4 mr-2" />
                {currentLanguage === 'de' ? 'AI Influencer' : 
                 currentLanguage === 'fr' ? 'Influenceur IA' :
                 currentLanguage === 'it' ? 'Influencer IA' : 'AI Influencer'}
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                {currentLanguage === 'de' ? 'Virtuelle Markenbotschafter der Zukunft' :
                 currentLanguage === 'fr' ? 'Ambassadeurs de Marque Virtuels du Futur' :
                 currentLanguage === 'it' ? 'Ambasciatori Virtuali del Brand del Futuro' :
                 'Virtual Brand Ambassadors of the Future'}
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                {currentLanguage === 'de' ? 'Erstellen Sie mit KI perfekte Influencer: 24/7 verfügbar, mehrsprachig, skandalfrei und 90% kostengünstiger' :
                 currentLanguage === 'fr' ? 'Créez des influenceurs parfaits avec IA: disponibles 24/7, multilingues, sans scandales et 90% moins chers' :
                 currentLanguage === 'it' ? 'Create influencer perfetti con IA: disponibili 24/7, multilingue, senza scandali e 90% più economici' :
                 'Create perfect influencers with AI: available 24/7, multilingual, scandal-free and 90% more cost-effective'}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary">
                  {currentLanguage === 'de' ? 'AI Influencer erstellen' :
                   currentLanguage === 'fr' ? 'Créer Influenceur IA' :
                   currentLanguage === 'it' ? 'Crea Influencer IA' :
                   'Create AI Influencer'}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button variant="outline" size="lg">
                  {currentLanguage === 'de' ? 'Demo ansehen' :
                   currentLanguage === 'fr' ? 'Voir Démo' :
                   currentLanguage === 'it' ? 'Vedi Demo' :
                   'View Demo'}
                </Button>
              </div>

              {/* Revolutionary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">24/7</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Content Creation' :
                     currentLanguage === 'fr' ? 'Création Contenu' :
                     currentLanguage === 'it' ? 'Creazione Contenuto' :
                     'Content Creation'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">4</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Sprachen' :
                     currentLanguage === 'fr' ? 'Langues' :
                     currentLanguage === 'it' ? 'Lingue' :
                     'Languages'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">90%</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Kostenersparnis' :
                     currentLanguage === 'fr' ? 'Économies' :
                     currentLanguage === 'it' ? 'Risparmio' :
                     'Cost Savings'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">0</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Skandale' :
                     currentLanguage === 'fr' ? 'Scandales' :
                     currentLanguage === 'it' ? 'Scandali' :
                     'Scandals'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'AI Influencer Services' :
                 currentLanguage === 'fr' ? 'Services Influenceurs IA' :
                 currentLanguage === 'it' ? 'Servizi Influencer IA' :
                 'AI Influencer Services'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {services.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                      <div className="text-center mb-4">
                        <Icon className="w-12 h-12 text-primary mx-auto mb-4" />
                        <h3 className="text-xl font-semibold mb-2">
                          {service.title[currentLanguage]}
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          {service.description[currentLanguage]}
                        </p>
                        <div className="text-lg font-bold text-primary mb-4">
                          {service.price}
                        </div>
                        <Button className="w-full">
                          {currentLanguage === 'de' ? 'Mehr erfahren' :
                           currentLanguage === 'fr' ? 'En savoir plus' :
                           currentLanguage === 'it' ? 'Scopri di più' :
                           'Learn More'}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Erfolgreiche Anwendungsfälle' :
                 currentLanguage === 'fr' ? 'Cas d\'Usage Réussis' :
                 currentLanguage === 'it' ? 'Casi d\'Uso di Successo' :
                 'Successful Use Cases'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {useCases.map((useCase, index) => (
                  <Card key={index} className="p-6">
                    <h3 className="text-xl font-semibold mb-2">
                      {useCase.title[currentLanguage]}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {useCase.description[currentLanguage]}
                    </p>
                    <div className="text-lg font-bold text-green-500">
                      {useCase.roi}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Warum AI Influencer?' :
                 currentLanguage === 'fr' ? 'Pourquoi Influenceurs IA?' :
                 currentLanguage === 'it' ? 'Perché Influencer IA?' :
                 'Why AI Influencers?'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {advantages.map((advantage, index) => {
                  const Icon = advantage.icon;
                  return (
                    <div key={index} className="text-center">
                      <Icon className="w-12 h-12 text-primary mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">
                        {advantage.title[currentLanguage]}
                      </h3>
                      <p className="text-muted-foreground">
                        {advantage.description[currentLanguage]}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              {currentLanguage === 'de' ? 'Bereit für Ihre AI Influencer Revolution?' :
               currentLanguage === 'fr' ? 'Prêt pour votre Révolution Influenceurs IA?' :
               currentLanguage === 'it' ? 'Pronti per la vostra Rivoluzione Influencer IA?' :
               'Ready for Your AI Influencer Revolution?'}
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {currentLanguage === 'de' ? 'Lassen Sie uns gemeinsam den perfekten virtuellen Markenbotschafter für Ihr Unternehmen erschaffen.' :
               currentLanguage === 'fr' ? 'Créons ensemble l\'ambassadeur de marque virtuel parfait pour votre entreprise.' :
               currentLanguage === 'it' ? 'Creiamo insieme l\'ambasciatore virtuale perfetto per la vostra azienda.' :
               'Let\'s create the perfect virtual brand ambassador for your business together.'}
            </p>
            <Button size="lg" asChild className="bg-gradient-to-r from-primary to-primary/90">
              <a href={`/${currentLanguage}/kontakt/`}>
                {currentLanguage === 'de' ? 'Influencer erstellen' :
                 currentLanguage === 'fr' ? 'Créer Influenceur' :
                 currentLanguage === 'it' ? 'Crea Influencer' :
                 'Create Influencer'}
                <ArrowRight className="ml-2" size={20} />
              </a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AIInfluencerService;